import { useState } from "react";

import type { ComponentRecord } from "../../types/components";

interface Props {
  component: ComponentRecord;
}

/**
 * Stage 8.5: warn when a component's spec values are placeholders.
 *
 * Seed components were inserted with structurally complete but
 * approximate values (``extraction_metadata.verified_pending=true``).
 * Until those are replaced with real datasheet values, the badge
 * makes it obvious to the engineer that the numbers shouldn't be
 * trusted for production sizing.
 */
export function VerifiedPendingBadge({ component }: Props) {
  const [open, setOpen] = useState(false);
  const meta = component.extraction_metadata as
    | { verified_pending?: boolean }
    | null;
  if (!meta?.verified_pending) return null;
  return (
    <span
      data-testid={`verified-pending-badge-${component.id}`}
      style={{ position: "relative", display: "inline-block" }}
    >
      <button
        type="button"
        data-testid={`verified-pending-trigger-${component.id}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        title="Spec values pending verification"
        style={badgeStyle}
      >
        ⚠️ Spec values pending verification
      </button>
      {open && (
        <div
          data-testid={`verified-pending-popover-${component.id}`}
          role="dialog"
          style={popoverStyle}
        >
          This component was seeded with placeholder values. Real
          datasheet values will be added once the datasheet is uploaded
          and processed.
        </div>
      )}
    </span>
  );
}

const badgeStyle: React.CSSProperties = {
  background: "#fef3c7",
  color: "#92400e",
  border: "1px solid #fde68a",
  borderRadius: 999,
  padding: "0.15rem 0.6rem",
  fontSize: "0.7rem",
  fontWeight: 500,
  cursor: "pointer",
};

const popoverStyle: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 6px)",
  left: 0,
  zIndex: 5,
  width: 260,
  background: "#fffbeb",
  color: "#78350f",
  border: "1px solid #fde68a",
  borderRadius: 8,
  padding: "0.7rem 0.85rem",
  fontSize: "0.8rem",
  lineHeight: 1.45,
  boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
};
